import { useCallback, useMemo } from 'react';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import useNestedOptimisticReorder from '@/data/hook/useNestedOptimisticReorder';
import { ActivityQuestionOrder } from '@/data/services/activityServices';
import Activity from '@/models/Activity';
import Exam from '@/models/Exam';
import ExamArea from '@/models/ExamArea';
import { Question } from '@/models/Question';
import { adapterActivityQuestionOrder } from '@/utils/questions/adapterActivityQuestionOrder';
import { DragDropList } from '../ui/custom/dataInput/DragDrop';
import ExamActivityRow from './ExamActivityRow';

type ExamAreaQuestionsListProps = {
  exam: Exam;
  examArea: ExamArea;
  examAreas: ExamArea[];
  activities: Activity[];
  cacheManager: ListCacheManager<Activity>;
  isUpdating: boolean;
  isReordering: boolean;
  onUpdate: (params: {
    examAreaId: number;
    activityId: number;
    data: Partial<Activity>;
  }) => Promise<Activity>;
  onDelete: (params: { examAreaId: number; activityId: number }) => void;
  onReorder: (params: {
    examAreaId: number;
    order: ActivityQuestionOrder[];
  }) => void;
};

export default function ExamAreaQuestionsList({
  exam,
  examArea,
  examAreas,
  activities,
  cacheManager,
  isUpdating,
  isReordering,
  onUpdate,
  onDelete,
  onReorder,
}: ExamAreaQuestionsListProps) {
  const childrenMap = useMemo(
    () =>
      Object.fromEntries(
        activities.map(
          a => [String(a.id), a.questions ?? []] as [string, Question[]],
        ),
      ),
    [activities],
  );

  const activityById = useMemo(
    () => new Map(activities.map(a => [a.id, a])),
    [activities],
  );

  const handleReorder = useCallback(
    (
      reorderedParents: Activity[],
      reorderedChildrenMap: Record<string, Question[]>,
    ) => {
      const order = adapterActivityQuestionOrder(
        reorderedParents,
        reorderedChildrenMap,
      );
      onReorder({ examAreaId: examArea.id, order });
    },
    [onReorder, examArea.id],
  );

  const {
    optimisticParents,
    optimisticChildrenMap,
    handleDragEnd,
    isDraggingParent,
    onBeforeCapture,
  } = useNestedOptimisticReorder({
    parents: activities,
    childrenMap,
    parentType: 'ACTIVITY',
    parentPrefix: 'activity',
    isReordering,
    onReorder: handleReorder,
  });

  return (
    <ConditionalRenderer condition={optimisticParents.length > 0}>
      <DragDropList
        onDragEnd={handleDragEnd}
        onBeforeCapture={onBeforeCapture}
        groupId={'activities' + `${examArea.id}`}
        type="ACTIVITY"
        customPlaceholder
        className="gap-0"
      >
        {optimisticParents.map((activity, index) => (
          <ExamActivityRow
            key={activity.id}
            isDraggable
            activity={{
              ...(activityById.get(activity.id) ?? activity),
              questions: optimisticChildrenMap[activity.id] ?? [],
            }}
            examArea={examArea}
            exam={exam}
            index={index}
            cacheManager={cacheManager}
            examAreas={examAreas}
            isUpdating={isUpdating}
            onUpdate={onUpdate}
            onDelete={onDelete}
            isDragging={isDraggingParent}
            isReordering={isReordering}
          />
        ))}
      </DragDropList>
    </ConditionalRenderer>
  );
}
